import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  normalizedPreflightTenantId,
  preflightMarketsQueryOptions,
} from "@/lib/queries";
import { createMarket, deleteMarket } from "../api/preflight.api";
import { invalidateMarketAggregates } from "../lib/market-cache";
import type {
  AddPreflightMarketInput,
  PreflightMarket,
  PreflightMarketWithCount,
} from "../types/preflight.types";

/** The tenant's market list with report counts, for both selectors and the manage panel (R7). */
export function usePreflightMarkets() {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const query = useQuery(preflightMarketsQueryOptions(activeTenantId));

  const markets = useMemo<PreflightMarketWithCount[]>(
    () => query.data?.markets ?? [],
    [query.data],
  );
  const reportCount = query.data?.reportCount ?? 0;

  return { ...query, markets, reportCount };
}

/**
 * A new or removed market changes both the selector list and what the aggregate covers,
 * so every write invalidates the market list and every cached insights scope (R17).
 */
function useMarketListInvalidation() {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  return () => {
    invalidateMarketAggregates(
      queryClient,
      normalizedPreflightTenantId(activeTenantId),
    );
  };
}

/** Add a catalogue entry. The already-added conflict surfaces in the caller's panel, not a toast. */
export function useCreateMarket() {
  const invalidateMarketData = useMarketListInvalidation();

  return useMutation<PreflightMarket, Error, AddPreflightMarketInput>({
    mutationFn: (input) => createMarket(input),
    onSuccess: invalidateMarketData,
    meta: { skipGlobalError: true },
  });
}

/** Remove a market. An in-use market is refused by the API rather than cascading (R16). */
export function useDeleteMarket() {
  const invalidateMarketData = useMarketListInvalidation();

  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteMarket(id),
    onSuccess: invalidateMarketData,
    meta: { skipGlobalError: true },
  });
}
